import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Card from "src/components/common/card/Card";
import Input from "src/components/ui/Input";
import Textarea from "src/components/ui/Textarea";
import Dropdown from "src/components/ui/Dropdown";
import TableV2 from "src/components/ui/TableV2";
import Button from "src/components/ui/Button";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as adjustmentOrderActions from "src/redux/slices/adjustmentOrderSlice";
import { toast } from "react-toastify";
import productApis from "src/api/product";
import { AdjustmentOrderProduct } from "src/types/adjustmentOrder.types";
import { Product } from "src/types/product.types";
import { Trash } from "lucide-react";

const AdjustmentForm: React.FC = () => {
  const { adjustmentId } = useParams();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const { create } = useAppSelector((state) => state.adjustmentOrder);

  // ------------------ State ------------------
  const [products, setProducts] = useState<Product[]>([]);
  const [adjustList, setAdjustList] = useState<AdjustmentOrderProduct[]>([]);
  const [remarks, setRemarks] = useState("");

  useEffect(() => {
    const getProducts = async () => {
      try {
        const res = await productApis.getProductList({
          page: 1,
          limit: 1000,
        });
        const list = res?.data?.data || res?.data || [];
        setProducts(list);
      } catch (error) {
        console.error("Error loading products:", error);
        toast.error("Failed to load products");
      }
    };

    getProducts();
  }, []);

  const handleClose = useCallback(() => {
    dispatch(adjustmentOrderActions.resetCreateAdjustmentOrder());
    navigate("/app/adjustment/list");
  }, [dispatch, navigate]);

  useEffect(() => {
    if (create.data) {
      toast.success("Stock adjustment created successfully!");
      handleClose();
    } else if (create.error) {
      toast.error(`Error: ${create.error}`);
    }
  }, [create, handleClose]);

  // ------------------ Add / Update / Remove ------------------
  const handleAddProduct = (productId: string) => {
    const product = products.find((p) => p._id === productId);
    if (!product) return;

    if (adjustList.some((a) => a.productId === productId)) {
      toast.warning("Product already added");
      return;
    }

    setAdjustList([
      ...adjustList,
      {
        productId: product._id || "",
        name: product.name || "",
        quantity: 1,
        direction: "in",
        reason: "",
      },
    ]);
  };

  const handleUpdateItem = (
    index: number,
    field: keyof AdjustmentOrderProduct,
    value: any
  ) => {
    const updated = [...adjustList];
    // @ts-ignore
    updated[index][field] = value;
    setAdjustList(updated);
  };

  const handleRemoveItem = (index: number) => {
    setAdjustList(adjustList.filter((_, i) => i !== index));
  };

  // ------------------ Submit ------------------
  const handleSubmit = () => {
    if (adjustList.length === 0) {
      toast.error("Please add at least one product adjustment");
      return;
    }

    if (adjustmentId) {
      toast.error("Editing adjustments is not supported yet");
      return;
    }

    const payload = {
      products: adjustList.map((a) => ({
        productId: a.productId,
        name: a.name,
        quantity: Number(a.quantity),
        direction: a.direction,
        reason: a.reason,
      })),
      remarks,
      createdBy: user?._id || "",
    };

    dispatch(adjustmentOrderActions.createAdjustmentOrderRequest(payload));
  };

  const pageTitle = adjustmentId ? "Edit Adjustment" : "Create Adjustment";

  return (
    <DashboardLayout
      meta={{
        title: adjustmentId
          ? "Edit Adjustment | Invyntra"
          : "Create Adjustment | Invyntra",
        description: "Create stock adjustments.",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title={pageTitle}
          showBack={!!adjustmentId}
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Adjustments", path: "/app/adjustment/list" },
            { label: pageTitle, path: "" },
          ]}
        />

        <Card>
          <div className="space-y-4">
            {/* ---------------- PRODUCTS ---------------- */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Adjust Inventory
              </h3>

              <Dropdown
                label="Add Product"
                options={products.map((p) => ({
                  label: `${p.name} (${p.sku || p.productId})`,
                  value: p._id || "",
                }))}
                onChange={(val) => handleAddProduct(val)}
                value=""
              />

              {adjustList.length > 0 && (
                <div className="mt-4">
                  <TableV2
                    columns={[
                      { key: "name", header: "Product" },
                      {
                        key: "quantity",
                        header: "Qty",
                        accessor: ({ row, rowIndex }) => (
                          <input
                            type="number"
                            className="w-20 border rounded-md px-2 py-1 text-sm text-center"
                            value={row.quantity}
                            onChange={(e) =>
                              handleUpdateItem(
                                rowIndex,
                                "quantity",
                                e.target.value
                              )
                            }
                          />
                        ),
                      },
                      {
                        key: "direction",
                        header: "Direction",
                        accessor: ({ row, rowIndex }) => (
                          <Dropdown
                            value={row.direction}
                            options={[
                              { label: "Add Stock", value: "in" },
                              { label: "Remove Stock", value: "out" },
                            ]}
                            onChange={(val) =>
                              handleUpdateItem(rowIndex, "direction", val)
                            }
                          />
                        ),
                      },
                      {
                        key: "reason",
                        header: "Reason",
                        accessor: ({ row, rowIndex }) => (
                          <input
                            type="text"
                            className="w-full border rounded-md px-2 py-1 text-sm"
                            placeholder="e.g. Damaged, Expired, Correction"
                            value={row.reason || ""}
                            onChange={(e) =>
                              handleUpdateItem(
                                rowIndex,
                                "reason",
                                e.target.value
                              )
                            }
                          />
                        ),
                      },
                      {
                        key: "action",
                        header: "",
                        accessor: ({ rowIndex }) => (
                          <button
                            onClick={() => handleRemoveItem(rowIndex)}
                            className="hover:bg-red-100 text-red-600 px-2 py-2 rounded-md"
                          >
                            <Trash size={16} />
                          </button>
                        ),
                      },
                    ]}
                    data={adjustList}
                  />
                </div>
              )}
            </div>

            {/* ---------------- REMARKS ---------------- */}
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <Textarea
                label="Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Additional notes..."
              />
            </div>

            {/* ---------------- ACTION BUTTONS ---------------- */}
            <div className="flex justify-end space-x-3 pb-2">
              <Button onClick={handleClose} variant="outline">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                isLoading={create.loading}
                disabled={!adjustList.length}
                className="bg-indigo-600 text-white"
              >
                {adjustmentId ? "Update Adjustment" : "Save Adjustment"}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdjustmentForm;
